import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { MappingType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useModalControl, useAlert, useDebouncedValue } from '@frontend/design-system';
import { AddEditAppointmentTypeModal } from '../../components/AppointmentTypesV3/add-edit-appointment-type-modal';
import { GlobalAppointmentTypesPaymentForm } from '../../components/AppointmentTypesV3/appointment-type-payment-fields/global-appointment-types-payment-form';
import { AppointmentTypesTable } from '../../components/AppointmentTypesV3/AppointmentTypesTable';
import { AppointmentTypeBulkColorAssignmentBanner } from '../../components/banners/appointment-type-bulk-color-assignment-banner';
import { ScheduleSettingsSection } from '../../components/settings';
import { useAppointmentTypesCustomizationAndFeatureFlagsHelper } from '../../hooks/use-appointment-types-customization-and-feature-flags-helper';
import { useCheckPaymentsEnabled } from '../../hooks/use-check-payments-enabled';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { useAppointmentTypesHelperShallowStore } from '../../stores/use-appointment-types-helper-store';
import { PageConfig } from '../../types';

type AppointmentTypeV3PageProps = {
  locationId?: string;
};

export const AppointmentTypeV3Page = ({ locationId }: AppointmentTypeV3PageProps) => {
  const { t } = useTranslation('schedule');
  const alert = useAlert();
  const { selectedLocationIds, isMultiLocation } = useSchedulingLocationInfo();
  const selectedLocationId = locationId || selectedLocationIds[0];

  const { setPaymentsEnabled, setIsOnlineSchedulingEnabled } = useAppointmentTypesHelperShallowStore(
    'setPaymentsEnabled',
    'setIsOnlineSchedulingEnabled'
  );

  const [selectedAppointmentType, setSelectedAppointmentType] = useState<AppointmentType | null>(null);

  const { modalProps: editModalProps, triggerProps: editModalTriggerProps } = useModalControl();

  const [pageConfig, setPageConfig] = useState<PageConfig>({
    page: 1,
    limit: 50,
    searchValue: '',
  });

  const debouncedSearchValue = useDebouncedValue(pageConfig.searchValue, 400);

  const { mutateAsync: archiveAppointmentType } = SchedulerV3.Mutations.useUpdateAppointmentTypeMutation({
    optimisticUpdate: true,
  });

  const listMappingsQuery = SchedulerV3.Queries.useListMappingsQuery({
    request: {
      locationIds: [selectedLocationId],
      mappingType: MappingType.PROVIDER_APPOINTMENT_TYPE,
      showInactive: true,
    },
    options: {
      enabled: !!selectedLocationId,
    },
  });

  const { isLoading: isLoadingCFInfo, isOnlineSchedulingEnabled } =
    useAppointmentTypesCustomizationAndFeatureFlagsHelper(selectedLocationId);

  const { isPaymentsEnabled } = useCheckPaymentsEnabled(selectedLocationId);

  useEffect(() => {
    setIsOnlineSchedulingEnabled(isOnlineSchedulingEnabled);
    setPaymentsEnabled(isPaymentsEnabled);
  }, [isPaymentsEnabled, isOnlineSchedulingEnabled]);

  const handleOnEdit = (appointmentType: AppointmentType) => {
    setSelectedAppointmentType(appointmentType);
    editModalTriggerProps.onClick();
  };

  const handleOnArchive = (appointmentType: AppointmentType) => {
    setSelectedAppointmentType(appointmentType);
    archiveAppointmentType({
      appointmentTypeId: appointmentType.id,
      childLocationId: isMultiLocation ? appointmentType.locationId : undefined,
      hidden: true,
      details: {
        ...appointmentType.details,
        act: !appointmentType.details?.act,
        sobs: false,
      },
    })
      .then((res) => {
        if (res) {
          alert.success(
            t('Appointment type {{status}} successfully', {
              status: !!appointmentType.details?.act ? 'archived' : 'unarchived',
            })
          );
        }
      })
      .catch(() => {
        alert.error(
          t('Failed to {{status}} appointment type', {
            status: !!appointmentType.details?.act ? 'archive' : 'unarchive',
          })
        );
      });
  };

  const editModalOnClose = () => {
    setSelectedAppointmentType(null);
    editModalProps.onClose();
  };

  const isLoading = isLoadingCFInfo || listMappingsQuery.isFetching;
  return (
    <ScheduleSettingsSection css={containerStyle} transparentInSmallMode>
      <AppointmentTypeBulkColorAssignmentBanner locationIds={[selectedLocationId]} />
      <div>
        {isPaymentsEnabled && isOnlineSchedulingEnabled && (
          <GlobalAppointmentTypesPaymentForm locationId={selectedLocationId} />
        )}
        <AppointmentTypesTable
          locationId={selectedLocationId}
          mappingList={listMappingsQuery.data?.mappings || []}
          isLoading={isLoading}
          pageConfig={pageConfig}
          setPageConfig={setPageConfig}
          onEdit={handleOnEdit}
          onArchive={handleOnArchive}
          isPaymentsEnabled={isPaymentsEnabled}
          debouncedSearchValue={debouncedSearchValue}
          isMultiLocation={isMultiLocation}
        />
        {selectedAppointmentType && (
          <AddEditAppointmentTypeModal
            data={selectedAppointmentType}
            locationId={selectedLocationId}
            {...editModalProps}
            onClose={editModalOnClose}
          />
        )}
      </div>
    </ScheduleSettingsSection>
  );
};

const containerStyle = css({
  position: 'relative',
  overflow: 'auto',
});
